import { match } from 'ts-pattern'

/**
 *
 * @param getHostname
 * @returns
 *
 *
 * Would you consider accessing the window.location.hostname an effect or a calculation?
 *
 * This is a good question about React effects and side effects in general. Let me explain why accessing window.location.hostname can be considered a calculation rather than an effect:
 * It's synchronous - Reading window.location.hostname is an immediate, synchronous operation that returns a value right away. It doesn't involve promises, callbacks, or any asynchronous operations.
 * It's stable - The hostname doesn't change during the lifetime of a page load. While it's technically possible to modify the URL using history API, the hostname itself remains constant until a new page load occurs.
 * It's idempotent - Reading the hostname multiple times will always return the same value within the same page context. It has no side effects on the system state.
 * No external system interaction - Unlike true side effects (like fetching data, subscribing to events, or modifying DOM), reading the hostname is just accessing a property that's already available in memory.
 * Looking at your code, the current implementation in getEnvironmentData is correct to treat it as a calculation. You're using it as a pure function that takes an optional parameter for getting the hostname (which is great for testing!) and returns a consistent result based on that input.
 * If this were a side effect, you'd typically see issues like:
 * - Needing cleanup
 * - Requiring async handling
 * - Causing system changes
 * - Being non-deterministic
 * None of these apply to reading window.location.hostname, so it's perfectly fine to treat it as a regular calculation rather than an effect.
 */
export const getEnvironmentData: (getHostname?: () => string) => EnvironmentData = (
  // eslint-disable-next-line no-restricted-properties -- default param with DI for testability
  getHostname = () => window.location.hostname
) => {
  const hostname = getHostname()

  return match(hostname)
    .with('localhost', () => localDevData)
    .with('app.pixee.ai', () => pixeeSaaSData)
    .with('codeguardian.getpixee.com', () => nttData)
    .otherwise(() => unknownData)
}

export type EnvironmentName = 'local-dev' | 'pixee-saas' | 'ntt' | 'unknown'

export type EnvironmentData = {
  name: EnvironmentName
  pageTitle: string
  faviconHref: string
  logoIconHref: string
  logoIconAlt: string
  githubAppLogin: string
  githubAppName: string
}

const localDevData: EnvironmentData = {
  name: 'local-dev',
  pageTitle: 'Pixee Platform',
  faviconHref: '/default/favicon.ico',
  logoIconHref: '/0-Icon-Trimmed.png',
  logoIconAlt: 'Pixee Logo',
  githubAppLogin: 'pixee-c5c93607aa1802b2',
  githubAppName: 'Pixeebot',
}

const pixeeSaaSData: EnvironmentData = {
  name: 'pixee-saas',
  pageTitle: 'Pixee Platform',
  faviconHref: '/default/favicon.ico',
  logoIconHref: '/0-Icon-Trimmed.png',
  logoIconAlt: 'Pixee Logo',
  githubAppLogin: 'pixeebot',
  githubAppName: 'Pixeebot',
}

const nttData: EnvironmentData = {
  name: 'ntt',
  pageTitle: 'Code Guardian™',
  faviconHref: '/ntt-logo.png',
  logoIconHref: '/ntt-logo.png',
  logoIconAlt: 'NTT Logo',
  githubAppLogin: 'ntt',
  githubAppName: 'Code Guardian™',
}

const unknownData: EnvironmentData = {
  name: 'unknown',
  pageTitle: 'Pixee Platform',
  faviconHref: '/default/favicon.ico',
  logoIconHref: '/0-Icon-Trimmed.png',
  logoIconAlt: 'Pixee Logo',
  githubAppLogin: 'pixeebot',
  githubAppName: 'Pixeebot',
}
